import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import jsQR from 'jsqr';

const QRScanner = ({ onResult, onError }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestAnimationRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    startCamera();
    
    return () => {
      stopCamera();
      if (requestAnimationRef.current) {
        cancelAnimationFrame(requestAnimationRef.current);
        requestAnimationRef.current = null;
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      setLoading(true);
      
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        // Fallback for older browsers or unsupported environments
        const errorMsg = 'Camera access is not supported in your browser. Please try with a modern browser or update your current one.';
        console.error(errorMsg);
        setError(errorMsg);
        setLoading(false);
        onError(new Error(errorMsg));
        return;
      }
      
      // Request video with camera access
      const constraints = { 
        video: { 
          facingMode: 'environment', // Use back camera if available
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', true); // Required for iOS Safari
        videoRef.current.setAttribute('autoplay', true);    // Explicitly set autoplay
        videoRef.current.setAttribute('muted', true);       // Explicitly mute
        videoRef.current.play()
          .then(() => {
            console.log('Video playback started successfully');
          })
          .catch(playError => {
            console.error('Error starting video playback:', playError);
          });
        
        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {
          setLoading(false);
          requestAnimationRef.current = requestAnimationFrame(scanQRCode);
        };
      }
      
      streamRef.current = stream;
    } catch (err) {
      console.error('Error starting camera:', err);
      let errorMessage = 'Could not access camera';
      let isPermissionIssue = false;
      
      // More descriptive error messages based on error type
      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera device found on your device';
      } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera access denied. Please grant permission to use your camera';
        isPermissionIssue = true;
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera is already in use by another application';
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage = 'Camera does not meet the required constraints';
      } else if (err.name === 'NotSupportedError') {
        errorMessage = 'Camera API is not supported in this browser';
      } else if (err.name === 'TypeError' && err.message && err.message.includes('undefined is not an object')) {
        // Specific fix for the iOS "undefined is not an object" error
        errorMessage = 'Camera API not accessible on this device or browser';
        
        // Check if it's likely an iOS in-app browser
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        if (isIOS) {
          errorMessage += '. Try opening in Safari instead of an in-app browser';
        }
      }
      
      // Add device-specific troubleshooting for permission issues
      if (isPermissionIssue) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isAndroid = /Android/.test(navigator.userAgent);
        
        if (isIOS) {
          errorMessage += '. On iOS, go to Settings > Safari > Camera and enable access';
        } else if (isAndroid) {
          errorMessage += '. On Android, check your app permissions in Settings > Apps';
        }
      }
      
      setError(`Camera error: ${errorMessage}`);
      setLoading(false);
      onError(err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      streamRef.current = null;
    }
  };

  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current) {
      requestAnimationRef.current = requestAnimationFrame(scanQRCode);
      return;
    }
    
    const video = videoRef.current;
    
    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      // Not enough data to process yet
      requestAnimationRef.current = requestAnimationFrame(scanQRCode);
      return;
    }
    
    const canvas = canvasRef.current;
    const canvasContext = canvas.getContext('2d', { willReadFrequently: true });
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw current video frame to canvas
    canvasContext.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    try {
      // Get image data from canvas
      const imageData = canvasContext.getImageData(0, 0, canvas.width, canvas.height);
      
      // Process with jsQR
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });
      
      if (code) {
        // QR code detected - stop scanning and return result
        console.log("QR code detected:", code.data);
        onResult(code.data);
        return; // Stop the scanning loop if QR is found
      }
    } catch (err) {
      console.error("QR scan error:", err);
    }
    
    // Continue scanning
    requestAnimationRef.current = requestAnimationFrame(scanQRCode);
  };

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#000',
      borderRadius: 1,
      overflow: 'hidden'
    }}>
      {loading && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 10
        }}>
          <CircularProgress color="primary" />
          <Typography variant="body2" color="white" sx={{ mt: 2 }}>
            Starting camera...
          </Typography>
        </Box>
      )}
      
      {error && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 10,
          padding: 2
        }}>
          <Typography variant="body1" color="error" align="center">
            {error}
          </Typography>
          
          <Typography variant="body2" color="white" sx={{ mt: 2, textAlign: 'center' }}>
            If camera access continues to fail, you can scan using your device's native camera app 
            and manually enter the tree ID.
          </Typography>
        </Box>
      )}
      
      <video 
        ref={videoRef} 
        style={{ 
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
        autoPlay
        playsInline
        muted
      />
      
      <canvas 
        ref={canvasRef} 
        style={{ 
          display: 'none' // Hide canvas element, only used for processing
        }}
      />
      
      {/* QR code detection frame overlay */}
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        height: '80%',
        border: '2px solid #2E7D32',
        boxShadow: '0 0 0 100vmax rgba(0, 0, 0, 0.5)',
        boxSizing: 'border-box',
        zIndex: 5,
        pointerEvents: 'none',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '20px',
          height: '20px',
          borderTop: '5px solid #2E7D32',
          borderLeft: '5px solid #2E7D32',
          top: -2,
          left: -2
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '20px',
          height: '20px',
          borderTop: '5px solid #2E7D32',
          borderRight: '5px solid #2E7D32',
          top: -2,
          right: -2
        }
      }} />

      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '20px',
        height: '20px',
        borderBottom: '5px solid #2E7D32',
        borderLeft: '5px solid #2E7D32',
        zIndex: 6,
        pointerEvents: 'none'
      }} />

      <Box sx={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '20px',
        height: '20px',
        borderBottom: '5px solid #2E7D32',
        borderRight: '5px solid #2E7D32',
        zIndex: 6,
        pointerEvents: 'none'
      }} />
      
      <Typography 
        variant="body2" 
        color="white" 
        sx={{ 
          position: 'absolute', 
          bottom: 16, 
          left: 0, 
          right: 0, 
          textAlign: 'center',
          zIndex: 5,
          backgroundColor: 'rgba(0,0,0,0.5)',
          padding: 1
        }}
      >
        Position QR code within the frame
      </Typography>
    </Box>
  );
};

export default QRScanner; 